import { gql } from "@apollo/client";
import { faBookmark, faBroom, faCalendar, faCartArrowDown, faFileInvoiceDollar, faPlus, faTimeline } from "@fortawesome/free-solid-svg-icons";
import HTMLCode from "components/common/HTMLCode";
import Translate from "components/common/Translate";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import Page, { PageActionsType } from "components/common/page/Page";
import { uuid } from "config/app-constants";
import { graph } from "config/axios-context";
import { AppRoutes, URI_PARAMETER_KEYS } from "config/router";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { AccommodationInvestmentDatalistPageQuery, AccommodationInvestmentDatalistPageQueryVariables, AccommodationWorkInvestmentFilterInput, AccommodationWorkInvestmentSortInput } from "types/graphql";
import { Roles } from "utilities/app-utilities";
import { DatalistFilterDefinitions, appendFilters } from "utilities/datalist/datalist-filter-utilities";
import { DatalistColumnDefinitions, getBottomClassName } from "utilities/datalist/datalist-utilities";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";

type Query = AccommodationInvestmentDatalistPageQuery;
type Variables = AccommodationInvestmentDatalistPageQueryVariables;
type DataItem = NonNullable<Query['accommodationWorkInvestments']>['items'][number];

const query = gql`
query AccommodationInvestmentDatalistPage($where: AccommodationWorkInvestmentFilterInput, $sort: [AccommodationWorkInvestmentSortInput!], $skip: Int, $take: Int) {
    accommodationWorkInvestments {
      items(where: $where, skip: $skip, take: $take, order: $sort) {
        id
        name,
        startDate,
        season {
          name
        }
        unit
        unitPrice
        total
        quantity
        investmentType
      }
      count(where: $where),
      totalSum(where: $where)
    }
  }
`;

const AccommodationInvestmentDatalistPage = () => {
    const { [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: targetSeasonId } = useParams<{ [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: string }>();
    const context = useAppContext();
    const t = context.t;
    const [totalSum, setTotalSum] = useState<number>(0);
    const pageActions: PageActionsType = [
        {
            icon: faBroom,
            name: "pages.accommodation.work.maintenance.list",
            title: "pages.accommodation.work.maintenance.list",
            link: () => {
                const url = targetSeasonId ? AppRoutes.ACCOMMODATION_SEASON_OVERVIEW_MAINTENANCE : AppRoutes.ACCOMMODATION_OVERVIEW_MAINTENANCE;
                const params = targetSeasonId ? { [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: targetSeasonId } : undefined;
                context.navigate(url, params);
            }
        },
        {
            icon: faPlus,
            name: 'add',
            link: AppRoutes.ACCOMMODATION_INVESTMENT_FORM,
            title: 'button.add',
            hide: !context.userHasRole(Roles.ACCOMMODATION_WORK_WRITE) || targetSeasonId ? true : false
        }
    ];

    const datalistConfig: DatalistPropsType<DataItem, AccommodationWorkInvestmentFilterInput, AccommodationWorkInvestmentSortInput> = {
        itemId(i) {
            return i.id;
        },
        name: 'accommodationWorkInvestmentsPage',
        dataSource: {
            request: async (params) => {
                let variables: Variables = {
                    where: params.filters,
                    sort: params.sort,
                    skip: params.skip,
                    take: params.take
                }

                if (params.searchText) {
                    let search: AccommodationWorkInvestmentFilterInput = {
                        or: [
                            { name: { contains: params.searchText } },
                            { season: { name: { contains: params.searchText } } },
                        ]
                    };

                    variables.where = appendFilters(variables.where, search);
                }

                if (targetSeasonId) {
                    variables.where = appendFilters(variables.where, {
                        seasonId: {
                            eq: Number(targetSeasonId)
                        }
                    });
                }

                try {
                    const response = await graph<Query, Variables>({
                        query,
                        variables
                    });

                    if (response.errors) {
                        throw response.errors;
                    }

                    setTotalSum(response.data!.accommodationWorkInvestments!.totalSum!);
                    return response.data!.accommodationWorkInvestments!;
                } catch (error: any) {
                    context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            },
            filter: targetSeasonId ? undefined : {
                id: uuid(),
                logic: 'and',
                filters: [DatalistFilterDefinitions.seasonFilterDefinition(context)]
            }
        },
        table: {
            columns: [
                {
                    name: 'id',
                    hide: true
                },
                {
                    name: 'startDate',
                    title: 'fields.date.name',
                    resolve: i => DateUtil.format(new Date(i.startDate), DateFormats.SHORT),
                    filter: {
                        type: 'dateOnly'
                    },
                    sort: true,
                    type: 'dateOnly',
                    className: 'sticky-col',
                    card: {
                        group: 'date-name',
                        title: '',
                        icon: faCalendar
                    }
                },
                {
                    name: 'name',
                    sort: true,
                    filter: {
                        type: "string"
                    },
                    card: {

                        title: '',
                        icon: faBookmark
                    }
                },
                {
                    name: 'quantity',
                    resolve: i => `${i.quantity}  ${t('measurementUnits.' + i.unit)}`,
                    sort: true,
                    filter: {
                        type: 'number'
                    },
                    card: {
                        group: 'price-group',
                        title: '',
                        resolve: i => `${i.quantity} * ${i.unitPrice} ${t('measurementUnits.' + i.unit)}`,
                        icon: faCartArrowDown
                    }
                },
                {
                    name: 'unitPrice',
                    resolve: i => `${i.unitPrice} € / ${t('measurementUnits.' + i.unit)}`,
                    sort: true,
                    filter: {
                        type: 'number'
                    },
                    card: {
                        hide: true,
                    }
                },
                {
                    name: 'total',
                    type: 'number',
                    resolve: i => <span>{i.total} <HTMLCode code="euro" /></span>,
                    sort: true,
                    filter: {
                        type: 'number'
                    },
                    card: {
                        group: 'price-group',
                        title: '',
                        icon: faFileInvoiceDollar
                    }
                },
                {
                    ...DatalistColumnDefinitions.seasonColumnDefinition(context),
                    card: {
                        group: 'date-name',
                        title: '',
                        icon: faTimeline
                    }
                }
            ],
            render: {
                footer: {
                    table: {
                        columns: [
                            {
                                name: "total-row",
                                value: <b><Translate i18nKey="fields.total.name" /></b>,
                                className: 'sticky-col'
                            },
                            {
                                name: "total-row-quantity",
                                colSpan: 3,
                            },
                            {
                                name: "total-row-price",
                                value: <span>{totalSum} <HTMLCode code="euro" /></span>
                            }
                        ],
                        bottom: getBottomClassName(true).table,
                    },
                    card: {
                        bottom: getBottomClassName(true).card,
                        element: <div>
                            <span className="pr-2"><b><Translate i18nKey="fields.total.name" />:</b></span>
                            <span>{totalSum} <HTMLCode code="euro" /></span>
                        </div>
                    }
                },
                actions: {
                    mutationPermissionRole: Roles.ACCOMMODATION_WORK_WRITE,
                    viewUrl: AppRoutes.ACCOMMODATION_INVESTMENT_VIEW,
                }
            }
        },
        onItemClick: {
            onClick: i => context.navigate(AppRoutes.ACCOMMODATION_INVESTMENT_VIEW, { id: i.id }),
            type: 'doubleClick'
        }
    }

    return (
        <Page wFull title="pages.accommodation.work.investment.list" actions={pageActions}>
            <Datalist {...datalistConfig} />
        </Page>
    );
};

export default AccommodationInvestmentDatalistPage;
